import { useEffect, useRef, useState } from 'react';
import {
  getInfo,
  getMissedCallMessages,
  resetMissedCallMessage,
  saveMissedCallMessage,
  updateLocation,
} from '../services/api';
import { track } from '../services/analytics';

const EDITORS = [
  {
    type: 'missed_call_no_message',
    title: 'Missed call, no message left',
    help: 'Sent when a patient calls and hangs up without leaving a message.',
  },
  {
    type: 'missed_call_message_left',
    title: 'Missed call, message left',
    help: 'Sent when a patient calls and leaves a voicemail.',
  },
];

// Mirrors the server's STOP strip (api/tasks.py), so the counter and preview match what is sent.
const STOP_RE = /(this is an automated message\.?\s*)?reply\s+stop(\s+to\s+(opt[\s-]?out|unsubscribe|stop(\s+receiving)?(\s+(texts|messages))?))?\.?/gi;
const cleanText = (text) => text.replace(STOP_RE, '').trim();

const errorText = (err, type) => {
  const data = err.response?.data;
  const fieldError = data?.[type];
  if (Array.isArray(fieldError) && fieldError.length) return fieldError[0];
  if (typeof fieldError === 'string') return fieldError;
  if (typeof data?.detail === 'string') return data.detail;
  if (Array.isArray(data) && data.length) return data[0];
  return "Couldn't save. Please try again.";
};

const AutomaticTexts = () => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [locations, setLocations] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState({});
  const [errors, setErrors] = useState({});
  const [saved, setSaved] = useState({});
  const [switchBusy, setSwitchBusy] = useState(null);
  const [switchError, setSwitchError] = useState({});

  const timers = useRef([]);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [messages, info] = await Promise.all([getMissedCallMessages(), getInfo()]);
        if (cancelled) return;
        setPayload(messages);
        setDrafts(
          Object.fromEntries(Object.entries(messages.messages).map(([t, m]) => [t, m.content])),
        );
        setLocations(info.locations || []);
        setIsAdmin(!!info.is_company_admin);
      } catch (err) {
        console.error('Failed to load automatic texts:', err);
        if (!cancelled) setLoadError("Couldn't load the automatic texts.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setFlag = (setter, type, value) => setter((prev) => ({ ...prev, [type]: value }));

  // Applies one editor's result only, so unsaved text in the other editor stays.
  const run = async (type, call, onDone) => {
    setFlag(setBusy, type, true);
    setFlag(setErrors, type, null);
    try {
      const data = await call();
      setPayload((prev) => ({ ...prev, ...data }));
      setDrafts((prev) => ({ ...prev, [type]: data.messages[type].content }));
      onDone?.();
    } catch (err) {
      setFlag(setErrors, type, errorText(err, type));
    } finally {
      setFlag(setBusy, type, false);
    }
  };

  const handleSave = (type) =>
    run(type, () => saveMissedCallMessage(type, drafts[type]), () => {
      setFlag(setSaved, type, true);
      timers.current.push(setTimeout(() => setFlag(setSaved, type, false), 3000));
    });

  const handleReset = (type) => run(type, () => resetMissedCallMessage(type));

  const handleSwitch = async (locationId, enabled) => {
    setSwitchBusy(locationId);
    setFlag(setSwitchError, locationId, false);
    try {
      const updated = await updateLocation(locationId, { textback_enabled: enabled });
      setLocations((prev) =>
        prev.map((loc) =>
          loc.id === locationId ? { ...loc, textback_enabled: updated.textback_enabled } : loc,
        ),
      );
      track('missed_call_text_toggled', { location_id: locationId, enabled: updated.textback_enabled });
    } catch (err) {
      console.error('Error saving missed-call text setting:', err);
      setFlag(setSwitchError, locationId, true);
    } finally {
      setSwitchBusy(null);
    }
  };

  if (loading) return null;
  if (loadError) {
    return (
      <section className="mb-8 rounded-lg border border-border bg-white p-6">
        <p className="text-sm text-red-600">{loadError}</p>
      </section>
    );
  }

  // Same wording and spacing the send task builds: text, a space, then the STOP line.
  const previewFor = (text, location, message) =>
    `${cleanText(
      // The unedited default drops its link line for a location with no link, as the send does.
      message.is_default && text === message.content && !location.effective_booking_url
        ? message.default_without_link
        : text,
    )
      .replace(/\{\{clinic_name\}\}/g, location.name)
      .replace(/\{\{clinic_phone\}\}/g, location.phone || '')
      .replace(/\{\{booking_url\}\}/g, location.effective_booking_url || '')} ${payload.stop_line}`;

  return (
    <section
      className="mb-8 rounded-lg border border-border bg-white p-6 shadow-sm"
      data-attr="automatic-texts"
    >
      <h2 className="text-xl font-semibold text-gray-900">Automatic texts</h2>
      <p className="mt-1 text-sm text-gray-600">
        When a call to a location is missed, Dentara texts the caller. These are the words
        patients receive. You can use {'{{clinic_name}}'}, {'{{clinic_phone}}'} and{' '}
        {'{{booking_url}}'} (set it in the Info menu).
        {!isAdmin && ' Ask your admin to change this.'}
      </p>

      <div className="mt-4">
        <h3 className="text-sm font-medium text-gray-700">Text missed callers</h3>
        <ul className="mt-2 divide-y divide-gray-100">
          {locations.map((location) => (
            <li key={location.id} className="flex flex-wrap items-center gap-3 py-2">
              <label className="inline-flex items-center gap-2 text-sm text-gray-900">
                <input
                  type="checkbox"
                  checked={!!location.textback_enabled}
                  onChange={(e) => handleSwitch(location.id, e.target.checked)}
                  disabled={
                    switchBusy === location.id || !location.messaging_enabled || !isAdmin
                  }
                  className="h-4 w-4 rounded border-border text-teal-700 focus:ring-teal-500 disabled:opacity-50"
                />
                {location.name}: {location.textback_enabled ? 'On' : 'Off'}
              </label>
              {!location.messaging_enabled && (
                <span className="text-xs text-gray-500">
                  Texting is not set up for this location. Ask Dentara to set it up.
                </span>
              )}
              {switchError[location.id] && (
                <span className="text-sm text-red-600">Couldn't save. Please try again.</span>
              )}
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {EDITORS.map(({ type, title, help }) => {
          const message = payload.messages[type];
          const draft = drafts[type] ?? '';
          const length = cleanText(draft).length;
          const over = length > payload.max_length;
          const unchanged = draft === message.content;
          return (
            <div key={type} className="min-w-0" data-attr={`automatic-text-${type}`}>
              <h3 className="text-sm font-medium text-gray-900">{title}</h3>
              <p className="text-xs text-gray-500">{help}</p>
              {isAdmin ? (
                <textarea
                  aria-label={title}
                  value={draft}
                  rows={4}
                  onChange={(e) => setDrafts((prev) => ({ ...prev, [type]: e.target.value }))}
                  className="mt-2 w-full rounded-md border border-border p-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                />
              ) : (
                <p className="mt-2 whitespace-pre-wrap rounded-md bg-background p-2 text-sm text-gray-900">
                  {message.content}
                </p>
              )}
              <p className="mt-1 rounded-md bg-gray-50 px-2 py-1 text-sm text-gray-500">
                {payload.stop_line}{' '}
                <span className="text-xs">(added automatically, cannot be edited)</span>
              </p>
              {isAdmin && (
                <p className={`mt-1 text-xs ${over ? 'font-medium text-red-600' : 'text-gray-500'}`}>
                  {length} / {payload.max_length}
                </p>
              )}
              <div className="mt-3 rounded-md border border-teal-100 bg-teal-50 p-3">
                <p className="text-xs font-medium uppercase tracking-wide text-teal-800">
                  Preview
                </p>
                {(locations.length ? locations : [{ name: 'Your clinic' }]).map((location) => (
                  <p key={location.id ?? 'sample'} className="mt-1 whitespace-pre-wrap text-sm text-gray-900">
                    {previewFor(isAdmin ? draft : message.content, location, message)}
                  </p>
                ))}
              </div>
              {errors[type] && (
                <p role="alert" className="mt-2 text-sm text-red-600">
                  {errors[type]}
                </p>
              )}
              {isAdmin && (
                <div className="mt-3 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => handleSave(type)}
                    disabled={busy[type] || over || unchanged || !length}
                    className="rounded-md bg-teal-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-teal-800 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {busy[type] ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleReset(type)}
                    disabled={busy[type] || message.is_default}
                    className="text-sm font-medium text-teal-700 hover:text-teal-800 disabled:opacity-50"
                  >
                    Reset to default
                  </button>
                  {saved[type] && (
                    <span className="text-sm font-medium text-green-700">Saved</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default AutomaticTexts;
